import { EXTENSION_NAME_PATTERN } from '@/config/constants';

/** All known view path segments that can appear after /{appCode}/ */
const KNOWN_VIEW_PATHS = new Set([
  // Messaging
  'inbox', 'compose', 'send-sms',
  // Call
  'calls', 'call-history', 'voicemail', 'recordings', 'call-queues',
  // Events
  'schedule', 'history', 'links',
  // Common
  'settings',
  // AI Actions
  'ai-actions',
  // Legacy routes that use ?appCode= query param
  'app', 'app.html', 'send-sms', 'send-sms.html', 'app-lite',
  // Dedicated top-level pages that use ?appCode= query param
  'ai-actions',
  // Legacy embedded LookUp iframe — host page mounts /lookupframe?appCode=lookupfor<saas>
  // (see public/legacy/js/firebase-things.js line 12401). Without this entry the
  // single-segment matcher would treat "lookupframe" itself as the appCode.
  'lookupframe',
]);

/** 3-segment sub-view paths: /{appCode}/{parent}/{child} */
const KNOWN_SUB_VIEW_PATHS = new Set([
  'inbox/kanban',
]);

/**
 * Parse appCode from URL.
 * Supports:
 *   - Universal:   /{appCode}/{viewPath}  (e.g. /twilioforzohocrm/inbox)
 *   - Legacy:      /{viewPath}/{appCode}  (e.g. /inbox/twilioforzohocrm)
 *   - Query param: ?appCode=twilioforzohocrm
 *
 * When a TPA name is given without a SaaS suffix (e.g. /whatsapp/inbox),
 * it defaults to "page" — meaning standalone TPA without any SaaS integration.
 *   /whatsapp/inbox  →  whatsappforpage
 */
export function getAppCodeFromURL(): string | null {
  const path = window.location.pathname;

  // 3-segment sub-view format: /{appCode}/{parent}/{child} (e.g. /whatsapp/inbox/kanban)
  const subViewMatch = path.match(/^\/([^/]+)\/([^/]+)\/([^/]+)\/?$/);
  if (subViewMatch) {
    const [, first, second, third] = subViewMatch;
    if (KNOWN_SUB_VIEW_PATHS.has(`${second}/${third}`)) {
      return normalizeAppCode(first);
    }
  }

  // Universal format: /{appCode}/{viewPath} or legacy /{viewPath}/{appCode}
  const universalMatch = path.match(/^\/([^/]+)\/([^/]+)\/?$/);
  if (universalMatch) {
    const [, first, second] = universalMatch;
    if (KNOWN_VIEW_PATHS.has(second)) {
      // /{appCode}/{viewPath}
      return normalizeAppCode(first);
    }
    if (KNOWN_VIEW_PATHS.has(first)) {
      // Legacy: /{viewPath}/{appCode}
      return normalizeAppCode(second);
    }
    // Neither is a known view path — treat first as appCode
    return normalizeAppCode(first);
  }

  // Bare /{appCode} — any single segment that's not a known view path
  const singleMatch = path.match(/^\/([^/]+)\/?$/);
  if (singleMatch) {
    const segment = singleMatch[1];
    if (!KNOWN_VIEW_PATHS.has(segment)) {
      return normalizeAppCode(segment);
    }
  }

  // Query param
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('appCode') || params.get('appcode') || null;
  return raw ? normalizeAppCode(raw) : null;
}

/**
 * Extract the view path from the URL.
 * Returns the view segment (e.g. 'inbox', 'calls', 'schedule', 'inbox/kanban') or null.
 */
export function getViewPathFromURL(): string | null {
  const path = window.location.pathname;

  // 3-segment sub-view: /{appCode}/{parent}/{child}
  const subViewMatch = path.match(/^\/([^/]+)\/([^/]+)\/([^/]+)\/?$/);
  if (subViewMatch) {
    const [, , second, third] = subViewMatch;
    const subPath = `${second}/${third}`;
    if (KNOWN_SUB_VIEW_PATHS.has(subPath)) return subPath;
  }

  const match = path.match(/^\/([^/]+)\/([^/]+)\/?$/);
  if (!match) return null;

  const [, first, second] = match;
  if (KNOWN_VIEW_PATHS.has(second)) return second;
  if (KNOWN_VIEW_PATHS.has(first)) return first;
  return null;
}

/**
 * If appCode doesn't contain "for" separator (e.g. "whatsapp"),
 * append "forpage" to treat it as standalone TPA.
 *   "whatsapp"           → "whatsappforpage"
 *   "twilioforzohocrm"   → "twilioforzohocrm"  (unchanged)
 */
function normalizeAppCode(code: string): string {
  if (EXTENSION_NAME_PATTERN.test(code)) return code;
  // No "for" separator — standalone TPA, default to "page"
  return `${code}forpage`;
}

/**
 * Parse extension name into TPA and SaaS codes
 * e.g., "twilioforzohocrm" -> { tpa: "twilio", saas: "zohocrm" }
 */
export function parseAppCode(appCode: string): {
  tpa: string;
  saas: string;
} | null {
  const match = appCode.match(EXTENSION_NAME_PATTERN);
  if (!match) return null;

  return {
    tpa: match[1].toLowerCase(),
    saas: match[2].toLowerCase(),
  };
}

/**
 * Resolve a short app code to its full name
 * Some integrations use abbreviated codes
 */
export function resolveShortAppCode(code: string): string {
  const shortCodeMap: Record<string, string> = {
    zcrm: 'zohocrm',
    hspot: 'hubspotcrm',
    pd: 'pipedrive',
    sf: 'salesforce',
    tw: 'twilio',
    mb: 'messagebird',
    wa: 'whatsapp',
  };

  return shortCodeMap[code.toLowerCase()] || code.toLowerCase();
}

/**
 * Build URL with query parameters
 */
export function buildUrl(base: string, params: Record<string, string | number | boolean | undefined>): string {
  const url = new URL(base, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      url.searchParams.set(key, String(value));
    }
  });
  return url.toString();
}

/**
 * Get all query parameters as an object
 */
export function getQueryParams(): Record<string, string> {
  const params: Record<string, string> = {};
  new URLSearchParams(window.location.search).forEach((value, key) => {
    params[key] = value;
  });
  return params;
}
